import { toastError, toastSuccess } from '@/components/toast/toast'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import {
  deletePriceSheet,
  getPriceSheet,
  updatePriceSheet
} from './generated/price-sheet/price-sheet'
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/navigation'
import { queryKeys } from './query-keys'
import {
  PriceSheetDTO,
  UpdatePriceSheetRequest,
  ProblemDetails
} from '@/api/generated/model'

export const useGetPriceSheet = (
  priceSheetID?: string
): UseQueryResult<PriceSheetDTO> => {
  const { session } = useAuthContext()

  return useQuery({
    queryKey: queryKeys.priceSheets.id(priceSheetID),
    queryFn: async () => {
      const response = await getPriceSheet(priceSheetID!)
      return response.data
    },
    enabled: !!session && !!priceSheetID
  })
}

export const useUpdatePriceSheet = () => {
  return useMutation({
    mutationFn: async (request: UpdatePriceSheetRequest) => {
      const response = await updatePriceSheet(request)
      return response.data
    },
    onError: (error) => {
      toastError('An unexpected error occurred while updating the price sheet.')
      // The caller consumes the rejection; preserve the original failure and
      // existing Sentry classification instead of relying on an unhandled promise.
      Sentry.captureException(error)
    }
  })
}

export const useDeletePriceSheet = () => {
  const router = useRouter()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (priceSheetID: string) => {
      const response = await deletePriceSheet(priceSheetID)
      return response.data
    },
    onSuccess: (_data, priceSheetID) => {
      toastSuccess('Successfully deleted the price sheet.')
      router.push('/pricesheets')
      queryClient.removeQueries({
        queryKey: queryKeys.priceSheets.id(priceSheetID)
      })
    },
    onError: (error) => {
      console.error(error)
      const fallback =
        'An unexpected error occurred while deleting the price sheet.'
      if (
        axios.isAxiosError<ProblemDetails>(error) &&
        error.response?.status === 400
      ) {
        toastError(error.response.data?.detail ?? fallback)
        return
      }
      toastError(fallback)
    }
  })
}
